/**
 * Product Recommendations Section JavaScript
 * Handles Swiper initialization and product recommendations API fetching
 */

(function() {
  'use strict';

  function parseAutoplayDelayMs(value, fallbackSeconds) {
    const parsedValue = parseFloat(value);
    if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
      return fallbackSeconds * 1000;
    }

    const normalizedSeconds = parsedValue > 10 ? parsedValue / 1000 : parsedValue;
    return normalizedSeconds * 1000;
  }

  class ProductRecommendations extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.productId = this.dataset.productId;
      this.url = this.dataset.url;
      this.layout = this.dataset.layout;
      this.swiperInstance = null;
      this.isInitialized = false;
      this.hasFetched = false;
      this.originalSlideCount = 0;
      this.visibilityObserver = null;
    }

    connectedCallback() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
      } else {
        this.init();
      }
    }

    init() {
      const hasRealProducts = this.querySelector('.product-recommendations__container .product-card:not(.product-card--placeholder), .product-recommendations__container .product-card2:not(.product-card--placeholder), .product-recommendations__container .product-card3:not(.product-card--placeholder), .product-recommendations__container .product-card4:not(.product-card--placeholder)');
      const whenVisible = window.theme?.whenVisible || ((element, callback) => callback(element));

      if (!hasRealProducts && !this.hasFetched) {
        this.visibilityObserver = whenVisible(this, () => this.fetchRecommendations());
      } else if (hasRealProducts) {
        this.classList.add('product-recommendations--loaded');
        this.visibilityObserver = whenVisible(this, () => this.initializeLayout());
      }
    }

    async fetchRecommendations() {
      if (this.hasFetched) return;
      this.hasFetched = true;

      try {
        const response = await fetch(this.url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const text = await response.text();
        const html = new DOMParser().parseFromString(text, 'text/html');
        const recommendationsContainer = html.querySelector('product-recommendations');

        if (!recommendationsContainer) {
          this.style.display = 'none';
          return;
        }

        const newContent = recommendationsContainer.querySelector('.product-recommendations__container');
        if (!newContent || !newContent.innerHTML.trim()) {
          this.style.display = 'none';
          return;
        }

        const hasProducts = newContent.querySelector('.product-recommendations__grid, .product-recommendations__slider-container');
        if (!hasProducts) {
          this.style.display = 'none';
          return;
        }

        const currentContainer = this.querySelector('.product-recommendations__container');
        if (!currentContainer) return;

        currentContainer.innerHTML = newContent.innerHTML;
        this.classList.add('product-recommendations--loaded');
        this.isInitialized = false;
        this.originalSlideCount = 0;

        requestAnimationFrame(() => {
          this.initializeLayout();
        });
      } catch (error) {
        console.error('Error fetching product recommendations:', error);
        this.style.display = 'none';
      }
    }

    initializeLayout() {
      if (this.layout === 'slider' && !this.isInitialized) {
        this.waitForSwiper();
      }
    }

    waitForSwiper() {
      window.loadThemeSwiper().then(() => {
        this.initSlider();
      }).catch((error) => {
        console.warn('Swiper failed to load for ProductRecommendations:', error);
      });
    }

    initSlider() {
      const swiperElement = this.querySelector('.swiper');
      if (!swiperElement || this.isInitialized) return;

      this.originalSlideCount = this.originalSlideCount || swiperElement.querySelectorAll('.swiper-slide').length;
      if (this.originalSlideCount === 0) return;

      const prevButton = this.querySelector('.product-recommendations__nav--prev');
      const nextButton = this.querySelector('.product-recommendations__nav--next');
      const paginationEl = this.querySelector('.product-recommendations__pagination');

      const slidesDesktop = parseInt(this.dataset.slidesDesktop, 10) || 4;
      const slidesTablet = parseInt(this.dataset.slidesTablet, 10) || 3;
      const slidesMobile = parseInt(this.dataset.slidesMobile, 10) || 2;
      const spaceBetween = parseInt(this.dataset.spaceBetween, 10) || 16;
      const spaceBetweenMobile = parseInt(this.dataset.spaceBetweenMobile, 10) || 12;
      const speed = parseInt(this.dataset.speed, 10) || 300;
      const showNavigation = this.dataset.showNavigation === 'true';
      const showPagination = this.dataset.showPagination === 'true';
      const loopEnabled = this.dataset.loop !== 'false';
      const loop = loopEnabled && this.originalSlideCount > Math.max(slidesDesktop, slidesTablet, slidesMobile);
      const autoplayEnabled = this.dataset.autoplay === 'true';
      const autoplay = autoplayEnabled
        ? {
            delay: parseAutoplayDelayMs(this.dataset.autoplayDelay, 4),
            disableOnInteraction: false,
            pauseOnMouseEnter: this.dataset.autoplayPauseOnHover !== 'false'
          }
        : false;

      this.swiperInstance = new Swiper(swiperElement, {
        slidesPerView: slidesDesktop,
        spaceBetween: spaceBetween,
        speed: speed,
        loop: loop,
        watchOverflow: true,
        grabCursor: true,
        keyboard: {
          enabled: true
        },
        navigation: showNavigation && prevButton && nextButton
          ? {
              prevEl: prevButton,
              nextEl: nextButton
            }
          : false,
        pagination: showPagination && paginationEl
          ? {
              el: paginationEl,
              clickable: true
            }
          : false,
        autoplay: autoplay,
        breakpoints: {
          0: {
            slidesPerView: slidesMobile,
            spaceBetween: spaceBetweenMobile
          },
          750: {
            slidesPerView: slidesTablet,
            spaceBetween: spaceBetween
          },
          990: {
            slidesPerView: slidesDesktop,
            spaceBetween: spaceBetween
          }
        },
        on: {
          init: () => {
            this.isInitialized = true;
            swiperElement.classList.add('swiper-initialized');
            this.syncNavigationAvailability();
          },
          resize: () => {
            this.syncNavigationAvailability();
          },
          breakpoint: () => {
            this.syncNavigationAvailability();
          },
          lock: () => {
            this.syncNavigationAvailability();
          },
          unlock: () => {
            this.syncNavigationAvailability();
          }
        }
      });

      this.syncNavigationAvailability();
    }

    syncNavigationAvailability() {
      const prevButton = this.querySelector('.product-recommendations__nav--prev');
      const nextButton = this.querySelector('.product-recommendations__nav--next');
      const showNavigation = this.dataset.showNavigation === 'true';

      if (!this.swiperInstance || !prevButton || !nextButton || !showNavigation) {
        this.classList.remove('product-recommendations--can-navigate');
        return;
      }

      const currentSlidesPerView = this.getCurrentSlidesPerView();
      const hasOverflow = this.originalSlideCount > currentSlidesPerView;

      this.classList.toggle('product-recommendations--can-navigate', hasOverflow);
    }

    getCurrentSlidesPerView() {
      const slidesPerView = this.swiperInstance?.params?.slidesPerView;
      const numericSlidesPerView = typeof slidesPerView === 'number'
        ? slidesPerView
        : parseFloat(slidesPerView);

      return Number.isFinite(numericSlidesPerView) && numericSlidesPerView > 0
        ? numericSlidesPerView
        : 1;
    }

    disconnectedCallback() {
      this.visibilityObserver?.disconnect?.();

      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
        this.swiperInstance = null;
      }

      this.isInitialized = false;
    }
  }

  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', ProductRecommendations);
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      const section = event.target.querySelector('product-recommendations');
      if (section && !section.isInitialized) {
        section.initializeLayout?.();
      }
    });

    document.addEventListener('shopify:section:unload', function(event) {
      const section = event.target.querySelector('product-recommendations');
      if (section && section.swiperInstance) {
        section.swiperInstance.destroy(true, true);
        section.swiperInstance = null;
        section.isInitialized = false;
      }
    });
  }
})();
